<!--
  One «Сводная информация» card: same chrome as pb-field-card; settings opens grouping controls (like parent field cards).
-->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { VIcons } from '@/shared/ui'
import type { DragPayload } from '../model/types'
import type { SummaryLayoutItem } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import { createDragPayload } from '../model/useDragDrop'

const ROW_COLS = 12
const MIN_SPAN = 1

const props = withDefaults(
  defineProps<{
    item: SummaryLayoutItem
    tableFieldId: string
    displayName: string
    fieldTypeBadge?: string
    /** Nested / other-type field in summary — teal badge + grouping settings (stripe stays amber for «Перечни»). */
    isDirectory?: boolean
    parentBadge?: string
  }>(),
  {
    fieldTypeBadge: '',
    isDirectory: false,
    parentBadge: '',
  },
)

const emit = defineEmits<{
  remove: [itemId: string]
  resize: [itemId: string, newSpan: number]
  dragstart: [payload: DragPayload, event: DragEvent]
  settings: [itemId: string]
  'update-label': [itemId: string, label: string | undefined]
}>()

const rootRef = ref<HTMLElement | null>(null)
const isResizing = ref(false)
const previewSpan = ref<number | null>(null)

const isRenaming = ref(false)
const renameDraft = ref('')
const renameInputRef = ref<HTMLInputElement | null>(null)

const gridColumnStyle = computed(() => {
  const span = previewSpan.value ?? props.item.length
  return { gridColumn: `span ${span}` as const }
})

const cardClass = computed(() => ({
  'pb-field-card--resizing': isResizing.value,
}))

function startRename() {
  isRenaming.value = true
  renameDraft.value = props.displayName
  nextTick(() => {
    renameInputRef.value?.focus()
    renameInputRef.value?.select()
  })
}

function cancelRename() {
  isRenaming.value = false
}

function commitRename() {
  if (!isRenaming.value) return
  isRenaming.value = false
  const v = renameDraft.value.trim()
  emit('update-label', props.item.id, v || undefined)
}

function onDragStart(event: DragEvent) {
  if (isResizing.value) {
    event.preventDefault()
    return
  }
  const payload = createDragPayload('grid', 'summary-item', {
    id: props.item.id,
    type: 'table',
    name: props.displayName,
    table_field_id: props.tableFieldId,
    summary_item_id: props.item.id,
  })
  emit('dragstart', payload, event)
}

function maxSpan(): number {
  return ROW_COLS
}

let startX = 0
let startLen = 0
let moveHandler: ((e: PointerEvent) => void) | null = null
let upHandler: (() => void) | null = null
let cancelHandler: (() => void) | null = null

function teardownResizeListeners() {
  if (moveHandler) document.removeEventListener('pointermove', moveHandler)
  if (upHandler) document.removeEventListener('pointerup', upHandler)
  if (cancelHandler) document.removeEventListener('pointercancel', cancelHandler)
  window.removeEventListener('blur', endResize)
  moveHandler = null
  upHandler = null
  cancelHandler = null
}

function endResize() {
  teardownResizeListeners()
  previewSpan.value = null
  isResizing.value = false
}

function onResizePointerDown(event: PointerEvent) {
  event.preventDefault()
  event.stopPropagation()

  isResizing.value = true
  startX = event.clientX
  startLen = props.item.length
  previewSpan.value = startLen

  const rowEl = rootRef.value?.closest('.pb-summary-subgrid') as HTMLElement | null
  const containerWidth = rowEl?.getBoundingClientRect().width ?? 0
  const colW = containerWidth > 0 ? containerWidth / ROW_COLS : 1

  moveHandler = (e: PointerEvent) => {
    if (!colW) return
    const deltaCols = Math.round((e.clientX - startX) / colW)
    const next = Math.max(MIN_SPAN, Math.min(maxSpan(), startLen + deltaCols))
    previewSpan.value = next
  }

  upHandler = () => {
    const finalSpan = previewSpan.value ?? startLen
    endResize()
    const clamped = Math.max(MIN_SPAN, Math.min(maxSpan(), finalSpan))
    if (clamped !== props.item.length) {
      emit('resize', props.item.id, clamped)
    }
  }

  cancelHandler = () => {
    endResize()
  }

  document.addEventListener('pointermove', moveHandler)
  document.addEventListener('pointerup', upHandler)
  document.addEventListener('pointercancel', cancelHandler)
  window.addEventListener('blur', endResize)
}

onBeforeUnmount(() => {
  teardownResizeListeners()
})
</script>

<template>
  <div
    ref="rootRef"
    class="pb-field-card pb-summary-field-card"
    :class="cardClass"
    :style="gridColumnStyle"
    role="article"
    :aria-label="`Сводная информация: ${displayName}`"
    :draggable="!isResizing && !isRenaming"
    @dragstart="onDragStart"
  >
    <div class="pb-fc-name">
      <input
        v-if="isRenaming"
        ref="renameInputRef"
        v-model="renameDraft"
        class="pb-summary-field-card__rename-input"
        type="text"
        :aria-label="'Имя поля'"
        @keydown.enter.prevent="commitRename"
        @keydown.escape.prevent="cancelRename"
        @blur="commitRename"
        @click.stop
        @mousedown.stop
      />
      <span
        v-else
        class="pb-summary-field-card__title"
      >
        <button
          type="button"
          class="pb-summary-field-card__title-btn"
          :aria-label="`Переименовать поле: ${displayName}`"
          title="Нажмите, чтобы переименовать"
          @click.stop="startRename"
        >
          {{ displayName }}
        </button>
      </span>
    </div>
    <div class="pb-fc-meta">
      <span v-if="fieldTypeBadge" class="pb-fc-type">{{ fieldTypeBadge }}</span>
      <span v-if="isDirectory && parentBadge" class="pb-fc-badge pb-fc-badge--teal">{{ parentBadge }}</span>
      <button
        type="button"
        class="pb-summary-field-card__settings-btn"
        aria-label="Открыть настройки группировки"
        @click.stop="emit('settings', item.id)"
      >
        <VIcons type="settings" class="pb-fc-settings" />
      </button>
    </div>

    <div
      class="pb-fc-resize"
      aria-hidden="true"
      @pointerdown="onResizePointerDown"
    />

    <VBtn
      class="pb-field-card-delete"
      icon
      variant="text"
      size="x-small"
      density="compact"
      aria-label="Удалить поле"
      @click.stop="emit('remove', item.id)"
    >
      <VIcons type="close" />
    </VBtn>
  </div>
</template>

<style scoped lang="scss">
.pb-summary-field-card__title {
  display: inline-flex;
  min-width: 0;
}

.pb-summary-field-card__title-btn {
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  cursor: text;
  text-align: left;

  &:focus-visible {
    outline: 2px solid var(--pb-primary, #1976d2);
    outline-offset: 1px;
    border-radius: 2px;
  }
}

.pb-summary-field-card__rename-input {
  width: 100%;
  min-width: 0;
  font: 500 11px/1.2 var(--pb-font-body, sans-serif);
  color: var(--pb-text-primary, rgba(0, 0, 0, 0.87));
  border: 1px solid var(--pb-primary, #1976d2);
  border-radius: 2px;
  padding: 1px 4px;
  outline: none;
  background: var(--pb-bg-canvas, #fff);
}

.pb-summary-field-card__settings-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;

  &:focus-visible {
    outline: 2px solid var(--pb-primary, #1976d2);
    outline-offset: 1px;
    border-radius: 2px;
  }

  .pb-fc-settings {
    pointer-events: none;
  }
}
</style>
